import { useEffect, useRef, useState } from "react";
import { Phone, MessageSquare, Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/lib/site-config";
import { services } from "@/lib/services-data";

interface FormEmbedProps {
  id?: string;
  className?: string;
}

/**
 * Estimate request form.
 *
 * When a LeadConnector form is configured in siteConfig.widgets, the original
 * embed is rendered untouched (the form_embed.js script controls its height).
 * Until those IDs are issued for this business, we render a native form that
 * composes the request as an email so the site still captures leads.
 */
const FormEmbed = ({ id = "inline-M5F4x1WSNvbFNKd4O70K", className = "" }: FormEmbedProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { formIframeSrc, formScriptSrc, formId, formName, formHeight } = siteConfig.widgets;

  useEffect(() => {
    if (!formIframeSrc) return;
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    }
  }, [formIframeSrc, formScriptSrc]);

  if (formIframeSrc) {
    return (
      <div ref={containerRef} className={className}>
        <iframe
          src={formIframeSrc}
          /* Seed the native height so the form doesn't collapse before
             form_embed.js takes over sizing via postMessage. */
          style={{
            width: "100%",
            height: formHeight,
            border: "none",
            borderRadius: 10,
            display: "block",
          }}
          id={id}
          data-layout="{'id':'INLINE'}"
          data-trigger-type="alwaysShow"
          data-trigger-value=""
          data-activation-type="alwaysActivated"
          data-activation-value=""
          data-deactivation-type="neverDeactivate"
          data-deactivation-value=""
          data-form-name={formName}
          data-height={String(formHeight)}
          data-layout-iframe-id={id}
          data-form-id={formId ?? undefined}
          title={formName}
          scrolling="no"
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} className={className}>
      <NativeQuoteForm formElementId={id} />
    </div>
  );
};

const inputClass =
  "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition";

const labelClass = "block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5";

const NativeQuoteForm = ({ formElementId }: { formElementId: string }) => {
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const name = String(data.get("name") ?? "");
    const phone = String(data.get("phone") ?? "");
    const email = String(data.get("email") ?? "");
    const service = String(data.get("service") ?? "");
    const area = String(data.get("area") ?? "");
    const details = String(data.get("details") ?? "");

    const subject = `Estimate request — ${service || "Cleaning"}${area ? ` in ${area}` : ""}`;
    const body = [
      `Name: ${name}`,
      `Phone: ${phone}`,
      `Email: ${email}`,
      `Service: ${service}`,
      `Area / address: ${area}`,
      "",
      "Details:",
      details,
    ].join("\n");

    window.location.href = `${siteConfig.emailHref}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  if (sent) {
    return (
      <div className="text-center py-8 px-2">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
          <CheckCircle className="h-7 w-7 text-primary" />
        </div>
        <h4 className="font-heading text-xl text-foreground font-bold mb-2">Almost there</h4>
        <p className="text-muted-foreground text-sm mb-6 max-w-xs mx-auto">
          Your email app should have opened with the request ready to send. Prefer to talk instead?
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          {/* No w-full here: the buttons are the flex children now. The column
              layout stretches them as before, and in a row they must stay at
              content width rather than both claiming the full container. */}
          <Button asChild className="gap-2 rounded-xl font-semibold">
            <a href={siteConfig.phoneHref}>
              <Phone className="h-4 w-4" /> Call {siteConfig.phone}
            </a>
          </Button>
          <Button asChild variant="outline" className="gap-2 rounded-xl font-semibold">
            <a href={siteConfig.smsHref}>
              <MessageSquare className="h-4 w-4" /> Text Us
            </a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form id={formElementId} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass} htmlFor={`${formElementId}-name`}>
            Name
          </label>
          <input
            id={`${formElementId}-name`}
            name="name"
            required
            autoComplete="name"
            className={inputClass}
            placeholder="Your name"
          />
        </div>
        <div>
          <label className={labelClass} htmlFor={`${formElementId}-phone`}>
            Phone
          </label>
          <input
            id={`${formElementId}-phone`}
            name="phone"
            type="tel"
            required
            autoComplete="tel"
            className={inputClass}
            placeholder="(904) 000-0000"
          />
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor={`${formElementId}-email`}>
          Email
        </label>
        <input
          id={`${formElementId}-email`}
          name="email"
          type="email"
          required
          autoComplete="email"
          className={inputClass}
          placeholder="you@email.com"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass} htmlFor={`${formElementId}-service`}>
            Service
          </label>
          <select id={`${formElementId}-service`} name="service" className={inputClass} defaultValue="">
            <option value="" disabled>
              Choose a service
            </option>
            {services.map((s) => (
              <option key={s.slug} value={s.title}>
                {s.title}
              </option>
            ))}
            <option value="Not sure yet">Not sure yet</option>
          </select>
        </div>
        <div>
          <label className={labelClass} htmlFor={`${formElementId}-area`}>
            City / Area
          </label>
          <input
            id={`${formElementId}-area`}
            name="area"
            className={inputClass}
            placeholder="Orange Park, Jacksonville…"
          />
        </div>
      </div>

      <div>
        <label className={labelClass} htmlFor={`${formElementId}-details`}>
          Tell us about your space
        </label>
        <textarea
          id={`${formElementId}-details`}
          name="details"
          rows={3}
          className={`${inputClass} resize-y`}
          placeholder="Bedrooms and bathrooms, square footage, how often you'd like service…"
        />
      </div>

      <Button type="submit" size="lg" className="w-full gap-2 rounded-xl font-semibold">
        Request Free Estimate <Send className="h-4 w-4" />
      </Button>

      <div className="flex items-center gap-2 pt-1">
        <div className="h-px flex-1 bg-border" />
        <span className="text-muted-foreground text-xs font-medium">or reach us directly</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button asChild type="button" variant="outline" className="w-full gap-2 rounded-xl font-semibold">
          <a href={siteConfig.phoneHref}>
            <Phone className="h-4 w-4" /> Call
          </a>
        </Button>
        <Button asChild type="button" variant="outline" className="w-full gap-2 rounded-xl font-semibold">
          <a href={siteConfig.smsHref}>
            <MessageSquare className="h-4 w-4" /> Text
          </a>
        </Button>
      </div>
    </form>
  );
};

export default FormEmbed;
